<template>
  <section class="mch-group-workspace">
    <!-- 顶栏操作和过滤框 -->
    <table-toolbar
      :search.sync="search"
      :show-search="true"
      :show-refresh="true"
      :show-export="true"
      :show-column-settings="true"
      :columns="columnOptions"
      :visible-columns.sync="visibleColumns"
      :default-columns="defaultColumns"
      :fixed-columns="['# ID', '商户组名称', '操作']"
      @refresh="handleReset"
      @export="handleExport"
    >
      <template slot="left">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddData" class="btn-action">新增商户组</el-button>
        <cache-reload reload-type="mchGroup" size="small" btn-label="重载NACOS-商户组" class="reload-wrapper" />
      </template>
    </table-toolbar>

    <!-- 表格主体 -->
    <table-page
      :loading="loading"
      :empty="tables.length === 0"
      :current-page.sync="currentPage"
      :page-size.sync="pageSize"
      :total="totalCount"
      :page-sizes="[10, 20, 30, 50, 100, 200]"
      :show-top-pagination="!!totalCount"
      :show-bottom-pagination="!!totalCount"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      @retry="handleReset"
    >
      <el-table :data="tables" stripe style="width: 100%" class="premium-table">
        <el-table-column key="col-id" prop="id" label="# ID" width="100">
          <template slot-scope="scope">
            <span class="cell-id">{{ scope.row.id }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-name" prop="name" label="商户组名称" min-width="180">
          <template slot-scope="scope">
            <el-link type="primary" class="cell-name-link" @click="handleUpdateData(scope.$index, scope.row)">
              {{ scope.row.name }}
            </el-link>
          </template>
        </el-table-column>

        <el-table-column key="col-type" v-if="visibleColumns.includes('type')" prop="type" label="支付平台" min-width="120">
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 1 ? 'success' : 'primary'" size="small" class="pay-platform-tag">
              {{ scope.row.type === 1 ? '微信' : '支付宝' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column key="col-appId" v-if="visibleColumns.includes('appId')" prop="appId" label="默认商户AppId" min-width="180">
          <template slot-scope="scope">
            <span class="cell-text-gray">{{ scope.row.appId }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-open" v-if="visibleColumns.includes('open')" prop="open" label="随机商户开关" min-width="130" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.open ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.open ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column key="col-data" v-if="visibleColumns.includes('data')" prop="data" label="随机商户数据" min-width="150">
          <template slot-scope="scope">
            <ext-text-popover :content="scope.row.data" v-if="scope.row.data" />
            <span v-else class="cell-text-gray">-</span>
          </template>
        </el-table-column>

        <el-table-column key="col-actions" label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-dropdown @command="handleActionCommand($event, scope.row, scope.$index)" trigger="hover">
              <el-button size="mini" class="action-btn" circle icon="el-icon-more-outline"></el-button>
              <el-dropdown-menu slot="dropdown" class="premium-dropdown-menu">
                <el-dropdown-item command="edit">
                  <i class="el-icon-edit"></i> 编辑商户组
                </el-dropdown-item>
                <el-dropdown-item command="delete" style="color: #ef4444;">
                  <i class="el-icon-delete" style="color: #ef4444;"></i> 删除商户组
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

    </table-page>

    <MchGroupDialog :visible.sync="dialogVisible" :formData="formData" :type="dialogType" @saved="handleSaved" />
  </section>
</template>

<script>
import api from '@/api/channel'
import MchGroupDialog from './GroupDialog.vue'
import CacheReload from '@/components/CacheReload/index.vue'
import ExtTextPopover from '@/components/ExtTextPopover/index.vue'
import TablePage from '@/components/TablePage/index.vue'
import TableToolbar from '@/components/TableToolbar/index.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'MchGroup',
  components: { CacheReload, MchGroupDialog, ExtTextPopover, TablePage, TableToolbar },
  created () {
    this.fetchData(this.pageSize, this.currentPage)
  },
  computed: {
    appId () {
      return this.$store.getters.appId
    },
    tables () {
      const search = this.search
      if (search) {
        return this.tableData.filter(data => {
          return Object.keys(data).some(key => {
            return String(data[key]).toLowerCase().indexOf(search.toLowerCase()) > -1
          })
        })
      }
      return this.tableData
    },
    columnOptions () {
      return [
        { prop: 'type', label: '支付平台' },
        { prop: 'appId', label: '默认商户AppId' },
        { prop: 'open', label: '随机商户开关' },
        { prop: 'data', label: '随机商户数据' }
      ]
    },
    defaultColumns () {
      return this.columnOptions.map(item => item.prop)
    }
  },
  watch: {
    appId (val, oldVal) {
      if (val === oldVal) {
        return
      }
      this.fetchData(this.pageSize, this.currentPage)
    }
  },
  data () {
    return {
      pageSize: 20,
      currentPage: 1,
      totalCount: 0,
      tableData: [],
      search: '',
      formData: {},
      dialogVisible: false,
      dialogType: 'add',
      loading: false,
      visibleColumns: ['type', 'appId', 'open', 'data']
    }
  },
  methods: {
    fetchData (n1, n2) {
      this.loading = true
      const params = {
        size: n1,
        page: n2
      }
      api.getMchGroupDataByPage(params).then(res => {
        const list = res.data
        this.totalCount = res.total
        this.tableData = list
      }).finally(() => {
        this.loading = false
      })
    },
    handleSizeChange (val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchData(val, this.currentPage)
    },
    handleCurrentChange (val) {
      this.currentPage = val
      this.fetchData(this.pageSize, val)
    },
    handleAddData () {
      this.dialogType = 'add'
      this.formData = {}
      this.dialogVisible = true
    },
    handleUpdateData (index, rowData) {
      this.dialogType = 'edit'
      const { data } = rowData
      this.formData = { ...rowData, data: data ? JSON.parse(data) : [] }
      this.dialogVisible = true
    },
    handleDelete (id, i) {
      this.$confirm('确定删除吗?', '提示', { type: 'warning' }).then(() => {
        api.deleteMchGroup(id).then(() => {
          this.tableData.splice(i, 1)
          this.$message.success('删除成功')
        })
      })
    },
    handleReset () {
      this.fetchData(this.pageSize, this.currentPage)
    },
    handleSaved () {
      this.fetchData(this.pageSize, this.currentPage)
    },
    handleExport () {
      const rows = this.tables.map(row => ({
        ID: row.id,
        商户组名称: row.name,
        支付平台: row.type === 1 ? '微信' : '支付宝',
        默认商户AppId: row.appId,
        随机商户开关: row.open ? '开启' : '关闭',
        随机商户数据: row.data || ''
      }))
      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '商户组')
      XLSX.writeFile(workbook, `商户组-${this.$moment().format('YYYYMMDDHHmmss')}.xlsx`)
    },
    handleActionCommand (command, row, index) {
      if (command === 'edit') {
        this.handleUpdateData(index, row)
      } else if (command === 'delete') {
        this.handleDelete(row.id, index)
      }
    }
  }
}
</script>

<style scoped>
.mch-group-workspace {
  padding: 16px 24px;
}

/* 过滤筛选卡片 */
.filter-card {
  margin-top: 0 !important;
  margin-bottom: 20px;
  padding: 16px 24px;
  border-radius: var(--border-radius-card);
  background: var(--bg-card);
  border: 1px solid #E2E8F0;
  box-shadow: var(--shadow-base);
}

.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.filter-left-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-right-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-action {
  flex-shrink: 0;
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.btn-action:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.reload-wrapper {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.reload-wrapper :deep(.el-button) {
  background-color: var(--bg-reload) !important;
  border-color: var(--border-reload) !important;
  color: var(--text-reload) !important;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 600 !important;
}

.reload-wrapper :deep(.el-button:hover) {
  background-color: var(--bg-reload-hover) !important;
  color: var(--text-reload-hover) !important;
  border-color: var(--border-reload-hover) !important;
}

.btn-reset {
  background-color: var(--bg-page) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.btn-reset:hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
  border-color: var(--border-hover) !important;
}

.btn-setting {
  flex-shrink: 0;
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  border-radius: 8px !important;
  height: 32px;
  padding: 0 16px;
}

.btn-setting:hover {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-hover) !important;
  color: var(--text-main) !important;
}

.search-input {
  width: 220px;
  flex-shrink: 0;
}

.search-input :deep(.el-input__inner) {
  border-radius: 8px;
  border-color: var(--border-color);
  height: 32px;
  line-height: 32px;
  font-weight: 500;
  background-color: var(--bg-card);
  color: var(--text-main);
  transition: all 0.2s ease;
}

.search-input :deep(.el-input__inner:hover) {
  border-color: var(--border-hover);
}

.search-input :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

/* 表格卡片样式 */
.table-card {
  padding: 20px;
  border-radius: var(--border-radius-card);
  background-color: var(--bg-card);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-base);
}

.premium-table {
  border-radius: 8px;
  overflow: hidden;
  border: none !important;
  color: var(--text-main) !important;
}

.premium-table::before {
  display: none !important;
}

.premium-table :deep(.el-table__header-wrapper) th,
.premium-table :deep(.el-table__fixed-header-wrapper) th,
.premium-table :deep(.el-table__fixed-right .el-table__fixed-header-wrapper) th {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  font-weight: 700 !important;
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-color) !important;
}

.premium-table :deep(.el-table__row) td {
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-light) !important;
}

.premium-table :deep(.el-table__row) {
  transition: background-color 0.2s ease;
}

.premium-table :deep(.el-table__row:hover) td,
.premium-table :deep(.el-table__body tr.hover-row > td),
.premium-table :deep(.el-table__body tr.hover-row.current-row > td),
.premium-table :deep(.el-table__body tr.hover-row.el-table__row--striped > td) {
  background-color: var(--bg-hover) !important;
}

.cell-id,
.cell-text-gray {
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace !important;
  font-size: 12.5px !important;
}

.cell-id {
  font-weight: 600;
  color: var(--text-muted);
}

.cell-text-gray {
  color: var(--text-muted);
}

.cell-name-link {
  font-size: 13.5px !important;
  font-weight: 700 !important;
  text-decoration: none !important;
}

.pay-platform-tag {
  font-weight: 600;
  border-radius: 6px;
}

/* 深色模式适配 */
html.dark .pay-platform-tag.el-tag--success {
  color: #34d399 !important;
  background-color: rgba(16, 185, 129, 0.15) !important;
  border-color: rgba(16, 185, 129, 0.3) !important;
}

html.dark .pay-platform-tag.el-tag--primary {
  color: #60a5fa !important;
  background-color: rgba(37, 99, 235, 0.15) !important;
  border-color: rgba(37, 99, 235, 0.3) !important;
}

/* 操作列三点图标 */
.action-btn {
  font-size: 12px;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  background: var(--bg-card);
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: var(--bg-hover);
  color: var(--text-main);
  border-color: var(--border-hover);
}

/* 分页器 */
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.premium-pagination :deep(.btn-prev),
.premium-pagination :deep(.btn-next),
.premium-pagination :deep(.el-pager li) {
  background-color: var(--bg-hover) !important;
  color: var(--text-muted) !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  margin: 0 3px !important;
}

.premium-pagination :deep(.el-pager li.active) {
  background-color: var(--primary-color) !important;
  color: #FFFFFF !important;
}

.premium-pagination :deep(.el-pagination__sizes .el-input__inner),
.premium-pagination :deep(.el-pagination__editor.el-input .el-input__inner) {
  border-radius: 6px;
  border-color: var(--border-color);
  background-color: var(--bg-card);
  color: var(--text-main);
}

/* 列配置 Popover 内部样式 */
.popover-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text-main);
  padding: 6px 4px;
}

.popover-divider {
  height: 1px;
  background: #E2E8F0;
  margin: 6px 0;
}

.popover-body-scroll {
  max-height: 250px;
  overflow-y: auto;
  padding: 4px;
}

.popover-body-scroll::-webkit-scrollbar {
  width: 4px;
}

.popover-body-scroll::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 2px;
}

.popover-body-scroll :deep(.el-checkbox) {
  display: block;
  margin-bottom: 8px;
  margin-right: 0;
}
</style>
